import React, { useEffect, useState } from 'react';
import { alpha } from '@mui/material/styles';
import {
  Box, Grid, Typography, Button, Dialog,
  DialogTitle, DialogContent, TextField, DialogActions, CircularProgress, useTheme,
} from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { api, type Project } from '../services/api';
import ProjectCard from '../components/ProjectCard';
import toast from 'react-hot-toast';

interface PendingDrop {
  projectId: string;
  projectName: string;
  file: File;
}

export default function ProjectsPage() {
  const theme = useTheme();
  const navigate = useNavigate();
  const [projects, setProjects] = useState<Project[]>([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ name: '', description: '', address: '', client_name: '' });
  const [saving, setSaving] = useState(false);

  // Drop-to-inspect flow state
  const [pendingDrop, setPendingDrop] = useState<PendingDrop | null>(null);
  const [inspectionTitle, setInspectionTitle] = useState('');
  const [inspectionLocation, setInspectionLocation] = useState('');
  const [analysing, setAnalysing] = useState(false);

  useEffect(() => {
    api.getProjects().then(setProjects).finally(() => setLoading(false));
  }, []);

  const createProject = async () => {
    if (!form.name.trim()) { toast.error('Project name is required'); return; }
    setSaving(true);
    try {
      const p = await api.createProject(form);
      setProjects(prev => [p, ...prev]);
      setOpen(false);
      setForm({ name: '', description: '', address: '', client_name: '' });
      toast.success('Project created');
    } catch { toast.error('Failed to create project'); }
    finally { setSaving(false); }
  };

  const handleDropOnProject = (projectId: string, file: File) => {
    const project = projects.find(p => p.id === projectId);
    if (!project) return;
    setPendingDrop({ projectId, projectName: project.name, file });
    setInspectionTitle(`Quick scan — ${new Date().toLocaleString()}`);
    setInspectionLocation('');
  };

  const cancelPendingDrop = () => {
    setPendingDrop(null);
    setInspectionTitle('');
    setInspectionLocation('');
  };

  const submitPendingDrop = async () => {
    if (!pendingDrop || !inspectionTitle.trim()) return;
    setAnalysing(true);
    try {
      const inspection = await api.createInspection({
        project_id: pendingDrop.projectId,
        title: inspectionTitle.trim(),
        location_description: inspectionLocation.trim() || undefined,
      });
      await api.analyzeImage(inspection.id, pendingDrop.file);
      toast.success('Inspection created and image analyzed');
      navigate(`/report/${inspection.id}`);
    } catch {
      toast.error('Could not create inspection from dropped image');
    } finally {
      setAnalysing(false);
      setPendingDrop(null);
      setInspectionTitle('');
      setInspectionLocation('');
    }
  };

  if (loading) return <Box sx={{ display: 'flex', justifyContent: 'center', mt: 10 }}><CircularProgress sx={{ color: 'primary.main' }} /></Box>;

  return (
    <Box sx={{ p: 3 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h5" sx={{ color: 'text.primary', fontWeight: 'bold' }}>Projects</Typography>
        <Button
          variant="contained"
          color="primary"
          onClick={() => setOpen(true)}>
          + New Project
        </Button>
      </Box>

      <Typography sx={{ color: 'text.secondary', fontSize: 12, mb: 2 }}>
        Tip: drop an image onto a project card to start a new inspection from that image.
      </Typography>

      <Grid container spacing={2}>
        {projects.map(project => (
          <Grid item xs={12} md={6} lg={4} key={project.id}>
            <ProjectCard
              project={project}
              onOpenInspections={(id) => navigate(`/inspections?project=${id}`)}
              onStartScan={(id) => navigate(`/scan?project=${id}`)}
              onDropImage={handleDropOnProject}
            />
          </Grid>
        ))}
      </Grid>

      {/* Create project dialog */}
      <Dialog open={open} onClose={() => setOpen(false)} PaperProps={{ sx: { bgcolor: 'background.paper', border: `1px solid ${alpha(theme.palette.text.primary, 0.04)}` } }}>
        <DialogTitle sx={{ color: 'text.primary' }}>New Project</DialogTitle>
        <DialogContent>
          {['name', 'client_name', 'address', 'description'].map(field => (
            <TextField
              key={field}
              fullWidth
              label={field.replace('_', ' ').replace(/\b\w/g, c => c.toUpperCase())}
              value={(form as any)[field]}
              onChange={e => setForm(f => ({ ...f, [field]: e.target.value }))}
              multiline={field === 'description'}
              rows={field === 'description' ? 3 : 1}
              required={field === 'name'}
              sx={{ mb: 2 }}
              InputLabelProps={{ sx: { color: 'text.secondary' } }}
            />
          ))}
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setOpen(false)} sx={{ color: 'text.secondary' }}>Cancel</Button>
          <Button
            onClick={createProject}
            disabled={saving}
            variant="contained"
            color="primary"
          >
            {saving ? <CircularProgress size={20} /> : 'Create'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Drop-to-inspect dialog */}
      <Dialog
        open={Boolean(pendingDrop)}
        onClose={analysing ? undefined : cancelPendingDrop}
        PaperProps={{ sx: { bgcolor: 'background.paper', border: `1px solid ${alpha(theme.palette.text.primary, 0.04)}`, minWidth: 420 } }}
      >
        <DialogTitle sx={{ color: 'text.primary' }} data-testid="drop-dialog-title">
          New Inspection on “{pendingDrop?.projectName}”
        </DialogTitle>
        <DialogContent>
          <Typography sx={{ color: 'text.secondary', fontSize: 13, mb: 2 }}>
            We will create an inspection and analyze {pendingDrop?.file.name} on it.
          </Typography>
          <TextField
            label="Title"
            fullWidth
            required
            value={inspectionTitle}
            onChange={(e) => setInspectionTitle(e.target.value)}
            inputProps={{ 'data-testid': 'drop-dialog-title-input' }}
            sx={{ mb: 2 }}
            InputLabelProps={{ sx: { color: 'text.secondary' } }}
          />
          <TextField
            label="Location (optional)"
            fullWidth
            value={inspectionLocation}
            onChange={(e) => setInspectionLocation(e.target.value)}
            sx={{}}
            InputLabelProps={{ sx: { color: 'text.secondary' } }}
          />
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={cancelPendingDrop} disabled={analysing} sx={{ color: 'text.secondary' }}>
            Cancel
          </Button>
          <Button
            onClick={submitPendingDrop}
            disabled={!inspectionTitle.trim() || analysing}
            variant="contained"
            color="primary"
            data-testid="drop-dialog-submit"
          >
            {analysing ? <CircularProgress size={20} /> : 'Create & Analyze'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
